import React, { useEffect, useRef } from "react";
import {
  Accordion,
  AccordionGroup,
  Banner,
  EmptyState,
  Flex,
  LoadingIndicator,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Text,
} from "@jsq/ds/core";
import { addAction } from "@jsq/observability";
import { FAY_RUM_ACTIONS } from "#constants";
import { useFayAuthGateArmed } from "#hooks/useFayAuthGate";
import { useSubmitFindingFeedback } from "#hooks/useSubmitFindingFeedback";
import { useTriageFinding } from "#hooks/useTriageFinding";
import { groupChecks, type CheckGroup } from "#utils/checkGrouping";
import {
  checkCountLabel,
  documentStatus,
  gatingMessage,
  humanizeLabel,
  sectionStatus,
} from "#utils/checksStatus";
import { docTypeLabel } from "#utils/classificationGrouping";
import { CheckRow } from "../CheckRow";
// The column widths are a shared contract between this header row and CheckRow's
// body cells — one fixed-layout table split across two components. They are
// imported rather than duplicated so the two halves cannot drift apart.
import { checkColumn, feedbackColumn, resultColumn } from "../CheckRow/styles_vanilla.css";
import * as styles from "./styles_vanilla.css";
import type { FeedbackRating } from "#graphql/supergraphTypes";
import type {
  ChecksTriageAction,
  DocumentSection,
  PackageDocument,
  PackageEnumerationStatus,
} from "#viewModelTypes";
import type { ApolloError } from "@apollo/client";

export type ChecksTabProps = {
  /** The package the checks belong to. `useTriageFinding` needs it to target
   *  the `PackageDetail` cache entry when patching triage state. Null while the
   *  slideout is closed / package hasn't loaded. */
  packageId: string | null;
  documents: readonly PackageDocument[];
  /** Package enumeration status: while `RUNNING`, the tab shows a processing
   *  state and the poll (`usePackageDetail`) refreshes results as they land;
   *  `COMPLETE`/null renders results, `FAILED` surfaces an error. */
  enumerationStatus: PackageEnumerationStatus | null;
  loading: boolean;
  error?: ApolloError;
  /** Re-reads the package after a triage write so state/counts refresh. */
  refetch: () => void;
  /** Fired when a document accordion is opened, to show that document's PDF in
   *  the companion panel at page 1 (the default). Omitted when no PDF panel is
   *  available. */
  onDocumentOpen?: (document: PackageDocument) => void;
  /** Fired when a section is opened, to jump the companion PDF to that section's
   *  first page. Omitted when no PDF panel is available. */
  onSectionOpen?: (document: PackageDocument, page: number) => void;
  /** Arena the triage and feedback writes target. */
  arenaId: number | undefined;
};

/** A single check-type group's Check / Result table. The subheader is shown only
 *  when the section spans more than one group. */
const CheckGroupTable: React.FC<{
  group: CheckGroup;
  showGroupHeader: boolean;
  isTriagePending: (findingId: string) => boolean;
  onTriage: (findingId: string, action: ChecksTriageAction) => void;
  isFeedbackPending: (findingId: string) => boolean;
  onSubmitFeedback: (findingId: string, rating: FeedbackRating, note?: string | null) => void;
}> = ({
  group,
  showGroupHeader,
  isTriagePending,
  onTriage,
  isFeedbackPending,
  onSubmitFeedback,
}) => {
  const table = (
    <Table condensed className={styles.fixedLayoutTable}>
      <TableHeader>
        <TableRow>
          <TableCell header className={checkColumn}>
            <Text variant="secondary-sm" asSpan>
              Check
            </Text>
          </TableCell>
          <TableCell header className={resultColumn}>
            <Text variant="secondary-sm" asSpan>
              Result
            </Text>
          </TableCell>
          {/* Empty header above the feedback (thumbs-down) column. Fixed
              60/20/20 widths keep columns aligned across every section's
              Table. */}
          <TableCell header className={feedbackColumn} />
        </TableRow>
      </TableHeader>
      <TableBody>
        {group.checks.map((check) => (
          <CheckRow
            key={check.checkId}
            check={check}
            isTriaging={check.findingId != null && isTriagePending(check.findingId)}
            onTriage={onTriage}
            isSubmittingFeedback={check.findingId != null && isFeedbackPending(check.findingId)}
            onSubmitFeedback={onSubmitFeedback}
          />
        ))}
      </TableBody>
    </Table>
  );
  // Single-group sections render exactly one table, unchanged. Only multi-group
  // sections get a subheader and the inter-group spacing wrapper.
  if (!showGroupHeader) {
    return table;
  }
  return (
    <div className={styles.groupSpacing}>
      <Text variant="regular-md" asSpan className={styles.groupHeader}>
        {group.name}
      </Text>
      {table}
    </div>
  );
};

/** A section's checks, sub-divided by check type into ordered groups. A single
 *  group renders exactly one table with no subheader; multiple groups each get a
 *  subheader row. */
const SectionChecks: React.FC<{
  section: DocumentSection;
  isTriagePending: (findingId: string) => boolean;
  onTriage: (findingId: string, action: ChecksTriageAction) => void;
  isFeedbackPending: (findingId: string) => boolean;
  onSubmitFeedback: (findingId: string, rating: FeedbackRating, note?: string | null) => void;
}> = ({ section, isTriagePending, onTriage, isFeedbackPending, onSubmitFeedback }) => {
  const groups = groupChecks(section.checks);
  const showGroupHeader = groups.length > 1;
  return (
    <>
      {groups.map((group) => (
        <CheckGroupTable
          key={group.name}
          group={group}
          showGroupHeader={showGroupHeader}
          isTriagePending={isTriagePending}
          onTriage={onTriage}
          isFeedbackPending={isFeedbackPending}
          onSubmitFeedback={onSubmitFeedback}
        />
      ))}
    </>
  );
};

/** Findings the tab puts in front of the reviewer, counted the same way
 *  `openFindingCount` counts them — one per enveloped check, open or not, since
 *  every one of them carries a feedback control. Sections start collapsed, so
 *  this is what the tab offers rather than what is on screen at one moment.
 *
 *  A gated document is skipped: the tab replaces its whole section tree with the
 *  gating banner, so any checks it carries have no feedback control and could
 *  never reach the numerator. The condition mirrors the render below and has to
 *  keep mirroring it.
 *
 *  Kept as one function because XFRG-1033 would regroup this tab by document and
 *  by section, which changes what a displayed-findings count means. */
export function findingsShownCount(
  documents: readonly PackageDocument[],
  fayMVPAugust: boolean,
): number {
  return documents
    .filter((document) => !(fayMVPAugust && document.gatingReason != null))
    .flatMap((document) => document.sections)
    .flatMap((section) => section.checks)
    .filter((check) => check.findingId !== null).length;
}

/**
 * ChecksTab — the package-detail Checks tab. Renders the package's checks
 * grouped by the tree the backend owns: document → section → check. Each
 * document is an accordion; opening it reveals its section accordions, each
 * holding a Check / Result table. A check row expands to its detail and triage
 * controls. A document with a failing status (or a lone document) starts
 * expanded so the reviewer lands on what needs attention; sections start
 * collapsed by default.
 */
export const ChecksTab: React.FC<ChecksTabProps> = ({
  packageId,
  documents,
  enumerationStatus,
  loading,
  error,
  refetch,
  onDocumentOpen,
  onSectionOpen,
  arenaId,
}) => {
  const { triage, isPending: isTriagePending } = useTriageFinding(refetch, packageId, arenaId);
  const { submit: submitFeedback, isPending: isFeedbackPending } = useSubmitFindingFeedback(
    refetch,
    packageId,
    arenaId,
  );
  // Fay MVP August release gate, read through the auth-gate context so this tab
  // doesn't require a FlagProvider when rendered on its own — see checksStatus'
  // packageStatus/packageSummary for its other uses. Off, a gated document
  // renders exactly as it did before gating shipped.
  const fayMVPAugust = useFayAuthGateArmed();

  // Only documents that actually carry checks belong on the Checks tab; a doc
  // still in classification has no sections yet. A gated document always belongs
  // once the gate is on, even if its checks haven't landed yet — it still needs
  // to show its Error state and gating message.
  const documentsWithChecks = documents.filter(
    (doc) =>
      (fayMVPAugust && doc.gatingReason != null) ||
      doc.sections.some((section) => section.checks.length > 0),
  );

  const isEnumerating = enumerationStatus === "RUNNING";
  const enumerationFailed = enumerationStatus === "FAILED";

  // Shared denominator for two rates over findings: engagement, whose numerator
  // is `useTriageFinding`, and dispute, whose numerator is
  // `useSubmitFindingFeedback`. One event per viewing of this tab, not per
  // render: `usePackageDetail` polls and every triage write refetches, so the
  // render body runs many times over for one viewing. Leaving and returning to
  // the tab remounts this component and counts a second viewing, which is the
  // intended unit — both rates answer "of the findings put in front of a
  // reviewer, how many drew this action". It waits for enumeration to settle,
  // because while RUNNING results land progressively and an early count would
  // understate what the reviewer ended up with.
  //
  // The count is a snapshot taken at the first settled render, so findings
  // enveloped later in the same viewing are missed. That is deliberate:
  // re-reporting on a changed count would put the same findings in the
  // denominator twice, which is the worse error. The gap is bounded by how much
  // can land after enumeration reports COMPLETE.
  const findingsShown = findingsShownCount(documentsWithChecks, fayMVPAugust);
  const reportedPackageId = useRef<string | null>(null);
  useEffect(() => {
    const displayingResults = !error && !isEnumerating && documentsWithChecks.length > 0;
    if (!displayingResults || packageId === null || reportedPackageId.current === packageId) {
      return;
    }
    reportedPackageId.current = packageId;
    addAction(FAY_RUM_ACTIONS.findingsDisplayed, {
      finding_count: findingsShown,
      package_id: packageId,
    });
  }, [error, isEnumerating, documentsWithChecks.length, packageId, findingsShown]);

  if (error) {
    return <Banner variant="danger" title="Couldn’t load checks" description={error.message} />;
  }

  if (loading && documentsWithChecks.length === 0) {
    return (
      <Flex alignItems="center" gap={8}>
        <LoadingIndicator size="small" />
        <Text variant="secondary-md" asSpan>
          Loading checks…
        </Text>
      </Flex>
    );
  }

  if (documentsWithChecks.length === 0) {
    // Enumeration in progress: hold on a processing state (the poll refreshes as
    // results land) rather than the "no checks" empty state, which would misread
    // an in-flight run as a package with nothing to review.
    if (isEnumerating) {
      return (
        <Flex alignItems="center" gap={8}>
          <LoadingIndicator size="small" />
          <Text variant="secondary-md" asSpan>
            Checks are running… this can take a minute.
          </Text>
        </Flex>
      );
    }
    if (enumerationFailed) {
      return (
        <Banner
          variant="danger"
          title="Checks didn’t run"
          description="The check run failed. Try re-running it from the package, or contact support if it keeps happening."
        />
      );
    }
    return (
      <EmptyState
        illustration="searching"
        title="No checks yet"
        description="This package has no check results to review yet."
      />
    );
  }

  const singleDocument = documentsWithChecks.length === 1;

  return (
    <Flex flexDirection="column" gap={12}>
      {isEnumerating && (
        <Flex alignItems="center" gap={8}>
          <LoadingIndicator size="small" />
          <Text variant="secondary-md" asSpan>
            Checks are still running — results update automatically.
          </Text>
        </Flex>
      )}
      {enumerationFailed && (
        <Banner
          variant="danger"
          title="Some checks didn’t finish"
          description="The check run ended before completing, so the results below may be partial."
        />
      )}
      <AccordionGroup>
        {documentsWithChecks.map((doc) => {
          const isGated = fayMVPAugust && doc.gatingReason != null;
          const docStatus = documentStatus(doc.sections, doc.gatingReason, fayMVPAugust);
          return (
            <Accordion
              key={doc.id}
              title={doc.filename}
              subtitle={docTypeLabel(doc.documentType) ?? undefined}
              statusText={docStatus.text}
              statusVariant={docStatus.variant}
              statusIconType={docStatus.icon}
              startExpanded={singleDocument || docStatus.variant === "error"}
              onToggle={(expanded) => {
                // Opening a document surfaces its PDF in the companion panel at
                // page 1 (the default). A subsequent section expand jumps the
                // page via onSectionOpen; this handler doesn't fight that.
                if (expanded) {
                  onDocumentOpen?.(doc);
                }
              }}
            >
              {isGated && doc.gatingReason != null ? (
                // The document's checks never ran, so its per-section skipped
                // rows are suppressed in favor of one gating-reason message.
                <Banner
                  variant="danger"
                  title="Error"
                  description={gatingMessage(doc.gatingReason)}
                />
              ) : (
                <AccordionGroup>
                  {doc.sections
                    .filter((section) => section.checks.length > 0)
                    .map((section) => {
                      const status = sectionStatus(section.checks);
                      return (
                        <Accordion
                          key={`${doc.id}:${section.key}`}
                          title={section.label ?? humanizeLabel(section.key)}
                          subtitle={checkCountLabel(section.checks.length)}
                          statusText={status.text}
                          statusVariant={status.variant}
                          statusIconType={status.icon}
                          startExpanded={false}
                          onToggle={(expanded) => {
                            // Opening a section jumps the companion PDF to its first
                            // page; sections with no page range (e.g. General) don't.
                            if (expanded && section.firstPage != null) {
                              onSectionOpen?.(doc, section.firstPage);
                            }
                          }}
                          disableBodyPadding
                        >
                          {/* Both writes report their own failures through a
                              toast and resync via `refetch`, so the promises
                              are intentionally unawaited. */}
                          <SectionChecks
                            section={section}
                            isTriagePending={isTriagePending}
                            onTriage={(findingId, action) => void triage(findingId, action)}
                            isFeedbackPending={isFeedbackPending}
                            onSubmitFeedback={(findingId, rating, note) =>
                              void submitFeedback(findingId, rating, note)
                            }
                          />
                        </Accordion>
                      );
                    })}
                </AccordionGroup>
              )}
            </Accordion>
          );
        })}
      </AccordionGroup>
    </Flex>
  );
};
